import {
    ArrowPathIcon,
    ArrowRightIcon,
    CheckCircleIcon,
} from '@heroicons/react/20/solid';
import { useEffect, useState } from 'react';
import { LoadingSpinner } from './LoadingSpinner.jsx';
import { useNotifications } from './context/NotificationContext/NotificationContext.jsx';
import { API } from './utils/api';
import * as versionModule from './version.js';

const CURRENT_EDITOR_VERSION =
    versionModule?.version ?? versionModule?.default?.version ?? '—';
const PARSED_CURRENT_EDITOR_VERSION = Number.parseInt(
    String(CURRENT_EDITOR_VERSION),
    10
);

/* Convert version number like 200 → 2.0, 211 → 2.11, 301 → 3.01 */
function formatVersion(v) {
    const num = parseInt(v, 10);
    if (isNaN(num)) return v;
    const major = Math.floor(num / 100);
    const minor = (num % 100).toString().padStart(2, '0');
    return `${major}.${minor}`;
}

export function UpdateEditor({ company_id, className = '' }) {
    const notifications = useNotifications();
    const [can_update, setCanUpdate] = useState(false);
    const [is_updating, setIsUpdating] = useState(false);
    const [currentVersion, setCurrentVersion] = useState(null);
    const [targetVersion, setTargetVersion] = useState(null);

    const displayVersion =
        currentVersion === null
            ? '—'
            : formatVersion(
                  can_update ? targetVersion ?? currentVersion : currentVersion
              );
    const versionLabel = `v${displayVersion}`;
    const expandedWidthPx = Math.max(62, 34 + versionLabel.length * 7);
    const revealVersionWidthPx = Math.max(24, versionLabel.length * 7);

    useEffect(() => {
        setCurrentVersion(
            Number.isFinite(PARSED_CURRENT_EDITOR_VERSION)
                ? PARSED_CURRENT_EDITOR_VERSION
                : CURRENT_EDITOR_VERSION
        );

        const checkForUpdate = async () => {
            try {
                const { version: master_version } = await API.get(
                    'editor/version'
                );
                const parsedMasterVersion = Number.parseInt(
                    String(master_version),
                    10
                );

                const normalizedMasterVersion = Number.isFinite(
                    parsedMasterVersion
                )
                    ? parsedMasterVersion
                    : master_version;
                setTargetVersion(normalizedMasterVersion);

                if (
                    Number.isFinite(parsedMasterVersion) &&
                    Number.isFinite(PARSED_CURRENT_EDITOR_VERSION)
                ) {
                    setCanUpdate(
                        parsedMasterVersion > PARSED_CURRENT_EDITOR_VERSION
                    );
                    return;
                }

                setCanUpdate(
                    String(master_version) !== String(CURRENT_EDITOR_VERSION)
                );
            } catch (error) {
                console.error(error);
            }
        };
        checkForUpdate();
    }, []);

    const handleUpdateEditor = async () => {
        if (is_updating) return;

        setIsUpdating(true);
        try {
            const forceResync = !can_update;
            const versionToSend = Number.parseInt(String(currentVersion), 10);
            const response = await API.post(
                `${company_id}/editor/update`,
                {
                    version: Number.isFinite(versionToSend) ? versionToSend : 0,
                    force: forceResync,
                },
                {
                    forceDirect: true,
                }
            );

            const responseMessage = String(response?.message || '');
            const responseVersionMatch =
                responseMessage.match(/version\s+(\d+)/i);
            const responseVersion = responseVersionMatch
                ? Number.parseInt(responseVersionMatch[1], 10)
                : null;
            const upgradedToVersion =
                Number.isFinite(responseVersion) && responseVersion > 0
                    ? responseVersion
                    : targetVersion;

            if (upgradedToVersion !== null && upgradedToVersion !== undefined) {
                setCurrentVersion(upgradedToVersion);
                setTargetVersion(upgradedToVersion);
            }
            setCanUpdate(false);

            notifications.push({
                title:
                    upgradedToVersion !== null &&
                    upgradedToVersion !== undefined
                        ? `Editor ${
                              forceResync ? 're-synced at' : 'updated to'
                          } v${formatVersion(upgradedToVersion)}!`
                        : 'Editor updated successfully!',
                variant: 'success',
                ttl: 10,
            });
        } catch (error) {
            const retryAfterSeconds =
                typeof error?.retryAfterSeconds === 'number'
                    ? error.retryAfterSeconds
                    : null;
            const isRateLimited =
                error?.status === 429 ||
                error?.code === 'GITHUB_SECONDARY_RATE_LIMIT';
            const isUpdateInProgress =
                error?.code === 'EDITOR_UPDATE_IN_PROGRESS';

            if (isUpdateInProgress) {
                notifications.push({
                    title: 'Editor update already running',
                    message: 'Please wait a minute before retrying.',
                    variant: 'success',
                    ttl: 20,
                });
                return;
            }

            notifications.push({
                title: isRateLimited
                    ? 'GitHub rate limit reached'
                    : 'Failed to update the editor!',
                message: isRateLimited
                    ? `Please retry in ${
                          retryAfterSeconds
                              ? `${retryAfterSeconds} seconds`
                              : 'a short while'
                      }.`
                    : undefined,
                variant: 'error',
                ttl: 20,
                error,
            });
        } finally {
            setIsUpdating(false);
        }
    };

    return (
        <button
            type="button"
            onClick={handleUpdateEditor}
            disabled={is_updating}
            aria-disabled={is_updating}
            aria-label={
                can_update
                    ? `Update editor to version ${displayVersion}`
                    : `Re-sync editor version ${displayVersion}`
            }
            className={[
                'group relative inline-flex h-7 w-7 shrink-0 items-center overflow-hidden rounded-full text-[9px] font-semibold leading-none text-white transition-[width,background-color] duration-300 ease-out focus-visible:outline-none',
                !is_updating
                    ? 'hover:w-[var(--update-editor-expanded-width)] focus-visible:w-[var(--update-editor-expanded-width)]'
                    : '',
                can_update
                    ? 'bg-[#8f52f1] hover:bg-[#7a43d4]'
                    : 'bg-white/20 hover:bg-[#8f52f1]',
                'cursor-pointer',
                is_updating ? 'cursor-wait' : '',
                className,
            ].join(' ')}
            style={{
                '--update-editor-expanded-width': `${expandedWidthPx}px`,
            }}
        >
            {is_updating ? (
                <span className="inline-flex w-full items-center justify-center">
                    <LoadingSpinner className="h-4 w-4" />
                </span>
            ) : (
                <span className="relative inline-flex w-full items-center">
                    <span className="absolute left-1.5 inline-flex h-4 w-4 items-center justify-center">
                        {can_update ? (
                            <>
                                <ArrowPathIcon className="h-4 w-4 opacity-100 transition-opacity duration-200 group-hover:opacity-0 group-focus-visible:opacity-0" />
                                <ArrowRightIcon className="absolute h-4 w-4 opacity-0 transition-opacity duration-200 group-hover:opacity-100 group-focus-visible:opacity-100" />
                            </>
                        ) : (
                            <CheckCircleIcon className="h-4 w-4 opacity-100" />
                        )}
                    </span>
                    <span
                        className="ml-7 inline-block max-w-0 overflow-hidden whitespace-nowrap opacity-0 transition-[max-width,opacity] duration-300 ease-out group-hover:max-w-[var(--update-editor-version-width)] group-hover:opacity-100 group-focus-visible:max-w-[var(--update-editor-version-width)] group-focus-visible:opacity-100"
                        style={{
                            '--update-editor-version-width': `${revealVersionWidthPx}px`,
                        }}
                    >
                        {versionLabel}
                    </span>
                </span>
            )}
        </button>
    );
}
